import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../common/Navbar";
import { mediaServices } from "../../data/mediaServices";
import "../../styles/servicePageTemplate.css";

const ServicePageTemplate = ({ service }) => {
  const stageRef = useRef(null);
  const navigate = useNavigate();

  const [phase, setPhase] = useState(0);
  const [typedChars, setTypedChars] = useState(0);
  const [showWave, setShowWave] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const currentIndex = mediaServices.findIndex((s) => s.slug === service.slug);
  const otherServices = mediaServices.filter((s) => s.slug !== service.slug);

  const titleText = service.title.toUpperCase();


  useEffect(() => {
    if (phase < 2 || typedChars >= titleText.length) return undefined;
    const t = setTimeout(() => setTypedChars((c) => c + 1), 25);
    return () => clearTimeout(t);
  }, [phase, typedChars, titleText.length]);

  const typingDone = phase >= 2 && typedChars >= titleText.length;

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setPhase(2);
      setTypedChars(titleText.length);
      setShowWave(false);
      setShowScrollBtn(true);
    } else {
      setPhase(0);
      setTypedChars(0);
      setShowWave(false);
      setShowScrollBtn(false);
    }
  }, [service.slug, titleText.length]);


  useEffect(() => {
    if (phase < 2) return undefined;
    const t = setTimeout(() => setShowWave(true), 600);
    return () => clearTimeout(t);
  }, [phase]);


  useEffect(() => {
    if (!showWave) return undefined;
    const t = setTimeout(() => setShowScrollBtn(true), 800);
    return () => clearTimeout(t);
  }, [showWave]);

  const handleWaveEnd = () => setShowWave(false);

  const handleTiltMove = (e) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--tilt-x", `${(-y * 16).toFixed(2)}deg`);
    el.style.setProperty("--tilt-y", `${(x * 16).toFixed(2)}deg`);
  };

  const handleTiltLeave = () => {
    const el = stageRef.current;
    if (!el) return;
    el.style.setProperty("--tilt-x", "0deg");
    el.style.setProperty("--tilt-y", "0deg");
  };

  return (
    <main className="svc">
      {phase < 2 &&
        createPortal(
          <div
            className={`svc-intro-overlay ${phase >= 1 ? "is-dark" : ""}`}
            aria-hidden="true"
          >
            <div className={`svc-intro-sphere ${phase >= 1 ? "is-hiding" : ""}`}>
              <span className="svc-intro-sphere-core" />
              <span className="svc-intro-sphere-ambient" />

              {phase === 0 && (
                <span
                  className="svc-intro-orbit"
                  onAnimationEnd={() => setPhase(1)}
                >
                  <span className="svc-intro-orbit-glow" />
                  <span className="svc-intro-orbit-core" />
                </span>
              )}

              {phase === 1 && (
                <span
                  className="svc-intro-wave-ring"
                  onAnimationEnd={() => setPhase(2)}
                />
              )}
            </div>
          </div>,
          document.body
        )}

      {showWave &&
        createPortal(
          <div
            className="svc-scrollwave"
            aria-hidden="true"
            onAnimationEnd={handleWaveEnd}
          >
            <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
              <path
                d="M0,0 L720,80 L1440,0"
                stroke="rgba(224,16,33,0.9)"
                strokeWidth="18"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>,
          document.body
        )}

      <Navbar />

      <section className="svc-hero">
        <span className="svc-hero-watermark" aria-hidden="true">
          {service.title}
        </span>
        <div
          className="svc-hero-bgimage"
          aria-hidden="true"
          style={{ backgroundImage: `url(${service.image})` }}
        />

        <div className="container position-relative z-1">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              {phase >= 2 && (
                <>
                  <p className="svc-eyebrow svc-reveal" style={{ animationDelay: "0s" }}>
                    <span className="svc-eyebrow-dot" />
                    <span className="svc-eyebrow-index">
                      {String(currentIndex + 1).padStart(2, "0")}
                    </span>
                    HKI Media Services
                  </p>

                  <h1 className="svc-title">
                    <span
                      style={{
                        visibility: typedChars > 0 ? "hidden" : "visible",
                        position: "absolute",
                      }}
                    >
                      {titleText[0]}
                    </span>
                    {titleText.slice(0, typedChars)}
                  </h1>

                  <h2
                    className="svc-hero-text svc-reveal-fade"
                    style={{
                      opacity: typingDone ? 1 : 0,
                      transform: typingDone ? "translateY(0)" : "translateY(14px)",
                      transitionDelay: "0.02s",
                    }}
                  >
                    {service.hero}
                  </h2>

                  <span
                    className="svc-hero-accent"
                    style={{
                      width: typingDone ? "44px" : "0px",
                      transitionDelay: "0.1s",
                    }}
                  />

                  {service.intro.split('\n').map((paragraph, i, arr) => (
                    paragraph.trim() && (
                      <p
                        key={i}
                        className="svc-intro svc-reveal-fade"
                        style={{
                          marginBottom: i === arr.length - 1 ? "30px" : "1rem",
                          opacity: typingDone ? 1 : 0,
                          transform: typingDone ? "translateY(0)" : "translateY(14px)",
                          transitionDelay: `${0.12 + i * 0.04}s`,
                        }}
                      >
                        {paragraph.trim()}
                      </p>
                    )
                  ))}

                  <div
                    className="svc-hero-cta-row svc-reveal-fade"
                    style={{
                      opacity: typingDone ? 1 : 0,
                      transform: typingDone ? "translateY(0)" : "translateY(14px)",
                      transitionDelay: "0.2s",
                    }}
                  >
                    <Link to="/contact-us" className="svc-btn svc-btn--primary">
                      Start a Project
                    </Link>
                    <a href="#svc-switcher" className="svc-btn svc-btn--ghost">
                      Explore Services
                    </a>
                  </div>
                </>
              )}
            </div>

            <div className="col-lg-6">
              {phase >= 2 && (
                <div
                  className="svc-stage svc-reveal"
                  ref={stageRef}
                  onMouseMove={handleTiltMove}
                  onMouseLeave={handleTiltLeave}
                  style={{ animationDelay: "0.05s" }}
                >
                  <div className="svc-stage-inner">
                    <span className="svc-stage-glow" aria-hidden="true" />
                    <div className="svc-stage-frame">
                      <img
                        className="svc-stage-img"
                        src={service.image}
                        alt={service.title}
                      />
                    </div>
                    <span className="svc-stage-shadow" aria-hidden="true" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="svc-scroll-btn"
          aria-label="Scroll to explore"
          onClick={() => window.scrollBy({ top: window.innerHeight, behavior: "smooth" })}
          style={{
            opacity: showScrollBtn ? 1 : 0,
            transform: showScrollBtn ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(12px)",
            pointerEvents: showScrollBtn ? "auto" : "none",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 6L12 14L20 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 13L12 21L20 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </section>

      <section className="svc-content">
        <div className="container">
          {service.sections.map((section, idx) => (
            <div
              className={`svc-block ${idx % 2 === 1 ? "svc-block--alt" : ""} ${
                section.items?.length ? "" : "svc-block--statement"
              }`}
              key={section.title}
              data-aos="fade-up"
            >
              <span className="svc-block-index" aria-hidden="true">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <h3 className="svc-block-title">{section.title}</h3>
              {section.text && <p className="svc-block-text">{section.text}</p>}

              {section.items?.length > 0 && (
                <div className="svc-list">
                  {section.items.map(([title, text], rowIdx) => (
                    <div
                      className="svc-row"
                      key={title}
                      data-aos="fade-up"
                      data-aos-delay={rowIdx * 40}
                    >
                      <span className="svc-row-num">
                        {String(rowIdx + 1).padStart(2, "0")}
                      </span>
                      <div className="svc-row-body">
                        <h4>{title}</h4>
                        <p>{text}</p>
                      </div>
                      <span className="svc-row-arrow" aria-hidden="true">
                        &rarr;
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section id="svc-switcher" className="svc-switcher">
        <div className="container">
          <div className="svc-switcher-track">
            {mediaServices.map((s) => (
              <button
                type="button"
                key={s.slug}
                className={`svc-switcher-pill ${
                  s.slug === service.slug ? "is-active" : ""
                }`}
                onClick={() => navigate(`/media-services/${s.slug}`)}
              >
                <img src={s.icon || s.image} alt="" aria-hidden="true" />
                <span>{s.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-cta">
        <div className="svc-glow svc-glow--cta" />
        <div className="container position-relative z-1 text-center" data-aos="zoom-in">
          <p className="svc-cta-text">
            {service.closing ||
              "Ready to bring this to life for your brand? Let's build something remarkable together."}
          </p>
          <Link to="/contact-us" className="svc-btn svc-btn--light">
            Let&rsquo;s Talk
          </Link>
        </div>
      </section>

      <section className="svc-more">
        <div className="container">
          <div className="svc-more-header" data-aos="fade-up">
            <h3>
              Explore Other <span>Services</span>
            </h3>
            <p>One ecosystem. Every capability your brand needs.</p>
          </div>

          <div className="svc-more-grid">
            {otherServices.map((s, i) => (
              <Link
                to={`/media-services/${s.slug}`}
                className="svc-more-card"
                key={s.slug}
                data-aos="fade-up"
                data-aos-delay={i * 40}
              >
                <img src={s.image} alt={s.title} />
                <div className="svc-more-scrim" />
                <div className="svc-more-body">
                  <h4>{s.title}</h4>
                  <span className="svc-more-link">
                    Discover <span aria-hidden="true">&rarr;</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicePageTemplate;